import { Panel } from '../core/Panel';
import {
  Sim, BASKET_CAP, GRANARY_CAP, PlotSystem, ConstructionSystem,
} from '../../game/simulation';
import { ICON } from '../icons';

export class StoragePanel extends Panel {
  /** Coords of the granary the player clicked to open this panel. */
  private focus: { c: number; r: number } | null = null;

  constructor(sim: Sim) { super(sim); }
  id() { return 'storage'; }
  title() { return 'Storage'; }
  setFocus(c: number, r: number) { this.focus = { c, r }; }

  hashInputs() {
    const rep = this.sim.storage();
    const all = PlotSystem.buildingsOfType(this.sim.state, 'granary');
    const grans = all.filter(g => ConstructionSystem.isOperational(g.tile)).length;
    const inProgress = all.length - grans;
    const focusTile = this.focus ? this.sim.state.plot.tiles[this.focus.r]?.[this.focus.c] : null;
    const r = this.sim.state.res;
    // Like MillPanel: include all stage-cost resources + this focus tile's
    // construction so the "Build next stage" button enables / advances
    // promptly when resources change.
    return JSON.stringify({
      rep, grans, inProgress, focus: this.focus,
      construction: focusTile?.construction ?? null,
      money: r.money, wood: r.wood, basket: r.basket, stone: r.stone ?? 0,
      seedsW: r.seeds?.wheat ?? 0,
      seedsC: r.seeds?.corn ?? 0,
      seedsK: r.seeds?.carrot ?? 0,
    });
  }
  body() {
    const rep = this.sim.storage();
    const all = PlotSystem.buildingsOfType(this.sim.state, 'granary');
    const grans = all.filter(g => ConstructionSystem.isOperational(g.tile)).length;
    const inProgress = all.length - grans;
    const pct = rep.totalCap > 0 ? Math.floor((rep.totalUsed / rep.totalCap) * 100) : 0;
    const focusTile = this.focus ? this.sim.state.plot.tiles[this.focus.r]?.[this.focus.c] : null;
    const focusValid = !!focusTile && focusTile.bld === 'granary';
    // Construction takeover — partial-deposit flow.
    if (focusValid && focusTile && focusTile.construction) {
      const cs = focusTile.construction;
      const cost = ConstructionSystem.nextStageCost(this.sim.state, this.focus!.c, this.focus!.r) ?? {};
      const paid = cs.stagePaid ?? { money: 0, wood: 0, wheat: 0, stone: 0 };
      const res = this.sim.state.res;
      const matRows: { label: string; need: number; paid: number; have: number }[] = [];
      if ((cost.money ?? 0) > 0) matRows.push({ label: `<span class="glyph glyph-inline">${ICON.coin}</span> Coins`, need: cost.money!, paid: paid.money, have: res.money });
      if ((cost.wood  ?? 0) > 0) matRows.push({ label: `<span class="glyph glyph-inline">${ICON.wood}</span> Wood`,  need: cost.wood!,  paid: paid.wood,  have: res.wood });
      if ((cost.stone ?? 0) > 0) matRows.push({ label: `<span class="glyph glyph-inline">${ICON.stone}</span> Stone`, need: cost.stone!, paid: paid.stone, have: res.stone ?? 0 });
      if ((cost.wheat ?? 0) > 0) matRows.push({ label: `<span class="glyph glyph-inline">${ICON.wheat}</span> Wheat`, need: cost.wheat!, paid: paid.wheat, have: res.basket });
      const canDeposit = matRows.some(m => m.paid < m.need && m.have > 0);
      const matsHTML = matRows.map(m => {
        const remaining = Math.max(0, m.need - m.paid);
        const pct = m.need > 0 ? Math.min(100, Math.round(m.paid / m.need * 100)) : 100;
        return `
          <div class="ui-row" title="${m.have} in storage">
            <span>${m.label}</span>
            <span>
              <strong>${m.paid} / ${m.need}</strong>
              <span class="ui-cargo__sub">${remaining > 0 ? `(need ${remaining}, have ${m.have})` : '✓'}</span>
            </span>
          </div>
          <div class="ui-bar"><div class="ui-bar__fill" style="width:${pct}%"></div></div>`;
      }).join('');
      return `
        <div class="ui-section">
          <div class="ui-section__title">Under construction</div>
          <div class="ui-row"><span>Stage</span><strong>${cs.stage} / ${cs.totalStages}</strong></div>
          <div class="ui-bar"><div class="ui-bar__fill" style="width:${(cs.stage / cs.totalStages) * 100}%"></div><div class="ui-bar__label">${Math.floor((cs.stage / cs.totalStages) * 100)}%</div></div>
        </div>
        <div class="ui-section">
          <div class="ui-section__title">Crate — next stage materials</div>
          ${matsHTML || '<div class="ui-tip">No materials required for the next stage.</div>'}
        </div>
        <button class="ui-btn ui-btn--primary ui-btn--block" data-act="advance" ${canDeposit ? '' : 'disabled'}
          title="Stages materials into the on-site crate. When the crate covers the full cost the stage advances automatically.">
          Deposit materials
        </button>
        <button class="ui-btn ui-btn--danger ui-btn--block" data-act="demolish">
          Cancel construction (50% refund)
        </button>
        <div class="ui-tip">Bring whatever you have — the crate stages partial deposits. The stage finishes the moment it's full.</div>
      `;
    }
    return `
      <div class="ui-section">
        <div class="ui-section__title">All crops</div>
        <div class="ui-bar"><div class="ui-bar__fill" style="width:${pct}%"></div><div class="ui-bar__label">${rep.totalUsed} / ${rep.totalCap} (${pct}%)</div></div>
      </div>
      <div class="ui-section">
        <div class="ui-section__title">Breakdown</div>
        <div class="ui-row"><span><span class="glyph glyph-inline">${ICON.wheat}</span> Wheat</span><strong>${rep.byCrop.wheat}</strong></div>
        <div class="ui-row"><span><span class="glyph glyph-inline">${ICON.corn}</span> Corn</span><strong>${rep.byCrop.corn}</strong></div>
        <div class="ui-row"><span><span class="glyph glyph-inline">${ICON.carrot}</span> Carrot</span><strong>${rep.byCrop.carrot}</strong></div>
      </div>
      <div class="ui-section">
        <div class="ui-section__title">Seeds</div>
        <div class="ui-row"><span><span class="glyph glyph-inline">${ICON.wheat}</span> Wheat seeds</span><strong>${this.sim.state.res.seeds?.wheat ?? 0}</strong></div>
        <div class="ui-row"><span><span class="glyph glyph-inline">${ICON.corn}</span> Corn seeds</span><strong>${this.sim.state.res.seeds?.corn ?? 0}</strong></div>
        <div class="ui-row"><span><span class="glyph glyph-inline">${ICON.carrot}</span> Carrot seeds</span><strong>${this.sim.state.res.seeds?.carrot ?? 0}</strong></div>
      </div>
      <div class="ui-section">
        <div class="ui-section__title">Granaries</div>
        <div class="ui-row"><span>${grans} operational</span><strong>+${grans * GRANARY_CAP} cap</strong></div>
        ${inProgress > 0 ? `<div class="ui-row"><span>${inProgress} under construction</span><strong>—</strong></div>` : ''}
      </div>
      ${rep.totalUsed >= rep.totalCap ? '<div class="ui-warn">⚠ Storage full — load the truck or build another Granary.</div>' : ''}
      ${grans === 0 ? `<div class="ui-tip">Basket only holds ${BASKET_CAP}. Build a Granary for +${GRANARY_CAP}.</div>` : ''}
      ${focusValid ? `
        <button class="ui-btn ui-btn--danger ui-btn--block" data-act="demolish">
          Demolish this Granary (50% refund)
        </button>
      ` : `
        <button class="ui-btn ui-btn--block" data-act="reposition">↔ Reposition storage shelf</button>
      `}
    `;
  }
  onClick(e: Event) {
    const t = e.target as HTMLElement;
    const actEl = t.closest('[data-act]') as HTMLElement | null;
    if (!actEl) return;
    // Reposition is a global action — works without a focused granary.
    if (actEl.dataset.act === 'reposition') {
      this.sim.startMoving('storage');
      this.hide();
      return;
    }
    if (!this.focus) return;
    if (actEl.dataset.act === 'demolish') {
      if (confirm('Tear down this Granary? You\'ll get half the cost back.')) {
        this.sim.demolish(this.focus.c, this.focus.r);
        this.hide();
      }
    } else if (actEl.dataset.act === 'advance') {
      // Partial-deposit flow — see MillPanel comment.
      this.sim.depositForBuilding(this.focus.c, this.focus.r);
    }
  }
}
